/**
 * Shared helpers for Gemini Google Search grounding metadata.
 */

import type { GenerateContentResult } from "firebase/ai";
import { isRedirectHost } from "./sourceUtils";
import type { GroundingSource } from "./types";

const DOMAIN_TITLE_RE = /^[\w-]+(\.[\w-]+)+$/i;

function resolveDomain(title: string, uri: string, apiDomain?: string): string | undefined {
  if (apiDomain) return apiDomain.replace(/^www\./, "");

  const trimmedTitle = title.trim();
  if (DOMAIN_TITLE_RE.test(trimmedTitle)) {
    return trimmedTitle.replace(/^www\./, "");
  }

  try {
    const host = new URL(uri).hostname.replace(/^www\./, "");
    if (!isRedirectHost(host)) return host;
  } catch {
    // fall through
  }

  return undefined;
}

export function extractGroundingSources(result: GenerateContentResult): GroundingSource[] {
  const metadata = result.response.candidates?.[0]?.groundingMetadata;
  const chunks = metadata?.groundingChunks ?? [];
  const supports = metadata?.groundingSupports ?? [];

  const snippetByIndex = new Map<number, string>();
  for (const support of supports) {
    const text = support.segment?.text?.trim();
    if (!text) continue;
    for (const index of support.groundingChunkIndices ?? []) {
      if (!snippetByIndex.has(index)) {
        snippetByIndex.set(index, text);
      }
    }
  }

  const sources: GroundingSource[] = [];
  for (const [index, chunk] of chunks.entries()) {
    const web = chunk.web;
    if (!web?.uri) continue;
    sources.push({
      title: web.title ?? web.uri,
      uri: web.uri,
      domain: resolveDomain(web.title ?? "", web.uri, web.domain),
      snippet: snippetByIndex.get(index),
    });
  }
  return sources;
}

export function mergeGroundingSources(
  target: GroundingSource[],
  incoming: GroundingSource[],
): void {
  for (const source of incoming) {
    const existing = target.find((s) => s.uri === source.uri);
    if (existing) {
      if (!existing.snippet && source.snippet) existing.snippet = source.snippet;
      if (!existing.domain && source.domain) existing.domain = source.domain;
      continue;
    }
    target.push(source);
  }
}
